import React, { useState, useEffect } from 'react'
import { 
  Box, 
  VStack, 
  HStack,
  Text, 
  Badge, 
  Button, 
  useColorModeValue,
  Icon,
  IconButton
} from '@chakra-ui/react'
import { FaClock, FaExpand, FaCompress } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { usePaywallContext } from '../contexts/PaywallContext'
import { useAuthContext } from '../contexts/AuthContext'

interface TrialCountdownProps {
  daysRemaining: number
}

export const TrialCountdown: React.FC<TrialCountdownProps> = ({ daysRemaining }) => {
  const navigate = useNavigate()
  const { userSubscription } = usePaywallContext()
  const { user } = useAuthContext()
  const [isMinimized, setIsMinimized] = useState(false)
  const bgColor = useColorModeValue('rgba(0, 0, 0, 0.8)', 'rgba(0, 0, 0, 0.8)')
  const cardBg = useColorModeValue('#191919', '#191919')

  // Fresh login detection: If no stored state exists OR last interaction was more than 24 hours ago,
  // treat it as a fresh login and start expanded. Otherwise, restore user's previous preference.
  useEffect(() => {
    const storedMinimized = localStorage.getItem('trialCountdownMinimized')
    const lastInteractionTime = localStorage.getItem('trialCountdownLastInteraction')
    
    // Check if this is a fresh login (no stored state or interaction was more than 24 hours ago)
    const isFreshLogin = !storedMinimized || !lastInteractionTime || 
      (Date.now() - parseInt(lastInteractionTime)) > (24 * 60 * 60 * 1000) // 24 hours
    
    if (isFreshLogin) {
      // Fresh login - start expanded
      setIsMinimized(false)
      localStorage.setItem('trialCountdownMinimized', 'false')
      localStorage.setItem('trialCountdownLastInteraction', Date.now().toString())
    } else {
      // User has interacted recently - restore their preference
      setIsMinimized(storedMinimized === 'true')
    }
  }, [])

  const toggleMinimize = () => {
    const newMinimizedState = !isMinimized
    setIsMinimized(newMinimizedState)
    // Store the user's preference and interaction timestamp
    localStorage.setItem('trialCountdownMinimized', newMinimizedState.toString())
    localStorage.setItem('trialCountdownLastInteraction', Date.now().toString())
  }

  const handleUpgrade = () => {
    navigate('/pricing')
  }

  // Check if user should see upgrade button
  // Show upgrade button for users who:
  // 1. Are in trial period (daysRemaining > 0)
  // 2. Skipped card verification (user.trial?.skippedVerification = true)
  // 3. Don't have an active subscription (userSubscription is null or status is not 'active')
  const shouldShowUpgradeButton = daysRemaining > 0 && 
    user?.trial?.skippedVerification && 
    (!userSubscription || userSubscription.status !== 'active')



  if (isMinimized) {
    return (
      <Box
        position="fixed"
        bottom={2}
        right={4}
        zIndex={9999}
        bg={cardBg}
        borderRadius="lg"
        p={2}
        border="1px solid"
        borderColor="yellow.400"
        boxShadow="lg"
      >
        <IconButton
          aria-label="Expand trial countdown"
          icon={<FaExpand />}
          size="sm"
          colorScheme="yellow"
          variant="ghost"
          onClick={toggleMinimize}
          color="yellow.400"
          _hover={{
            bg: "rgba(255, 255, 255, 0.1)"
          }}
        />
      </Box>
    )
  }

  return (
    <Box
      position="fixed"
      bottom={16}
      right={4}
      zIndex={9999}
      bg={cardBg}
      borderRadius="lg"
      p={4}
      border="1px solid"
      borderColor="yellow.400"
      boxShadow="lg"
      maxW="300px"
      minW="300px"
    >
      <VStack spacing={3} align="center">
        <HStack justify="space-between" w="full">
          <Icon as={FaClock} color="yellow.400" boxSize={6} />
          <Button
            size="xs"
            variant="ghost"
            onClick={toggleMinimize}
            color="gray.400"
            _hover={{
              color: "white"
            }}
            bg="transparent"
            px={2}
            py={1}
            fontSize="xs"
            fontWeight="normal"
            textDecoration="underline"
          >
            Hide
          </Button>
        </HStack>
        
        <Text fontSize="sm" color="gray.300" textAlign="center">
          Trial Period
        </Text>
        
        <Badge colorScheme="yellow" fontSize="lg" px={3} py={1}>
          {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
        </Badge>
        
        {/* Show upgrade text and button for users who skipped verification and don't have active subscription */}
        {shouldShowUpgradeButton && (
          <>
            <Text fontSize="xs" color="gray.400" textAlign="center">
              Upgrade to continue using all features
            </Text>
            
            <Button
              size="sm"
              colorScheme="yellow"
              onClick={handleUpgrade}
              w="full"
            >
              Upgrade Now
            </Button>
          </>
        )}
      </VStack>
    </Box>
  )
} 